import {useCallback, useState} from 'react';
import {useMutation} from '@apollo/client';

import {
    DELETE_SHIFT_PLANS,
    ENROLL_PERFORMER_FROM_SHIFT_PLANS,
    GENERATE_SHIFTS,
    UNENROLL_PERFORMER_FROM_SHIFT_PLANS,
    UPDATE_SHIFTS
} from '../queries/GraphQLQueries';
import {
    buildCurrentEnrollments,
    buildEnrolledPerformerIds,
    buildUnenrolledPerformerIds,
    isUnableToUnenrollUser,
    SHIFT_PLAN_ERROR_MESSAGE,
    SHIFT_PLAN_SUCCESS_MESSAGE
} from '../helpers/ShiftPlanHelper';
import {BOOKABLE_STATUS} from '../helpers/TimeblockHelper';

const mapBookableServiceTypesForShiftPlan = (shiftPlans, shiftBookableStatuses = {}) => {
    if (!shiftPlans || shiftPlans.length === 0) {
        return null;
    }

    const firstShiftPlan = shiftPlans[0];
    const selectedBookableStatuses = shiftBookableStatuses[firstShiftPlan.id] || [];

    const mappedBookableServiceTypes = firstShiftPlan.bookableServiceTypePlans?.map((plan, index) => ({
        id: plan.id,
        isBookable: (selectedBookableStatuses[index] || BOOKABLE_STATUS.BOOKABLE) === BOOKABLE_STATUS.BOOKABLE
    })) || [];

    return {
        shiftPlanId: firstShiftPlan.id,
        bookableServiceTypes: mappedBookableServiceTypes
    };
};

export default function useShiftPlanActions({
    shiftPlans,
    shouldRefreshShiftPlans,
    setIsProcessingShiftPlanData,
    shiftBookableStatuses
}) {
    const [alertMessage, setAlertMessage] = useState(null);
    const [isGeneratingShifts, setIsGeneratingShifts] = useState(false);
    const [isUpdatingShifts, setIsUpdatingShifts] = useState(false);

    const handleCloseCustomAlert = useCallback(() => setAlertMessage(null), []);

    const [enrollPerformer, {loading: isEnrolling}] = useMutation(ENROLL_PERFORMER_FROM_SHIFT_PLANS);
    const [unenrollPerformer, {loading: isUnenrolling}] = useMutation(UNENROLL_PERFORMER_FROM_SHIFT_PLANS);
    const [generateShifts] = useMutation(GENERATE_SHIFTS);
    const [updateShifts] = useMutation(UPDATE_SHIFTS);
    const [deleteShiftPlans] = useMutation(DELETE_SHIFT_PLANS);

    const handleError = (error) => {
        setAlertMessage({children: error?.message, severity: 'error'});
        console.log('error ' + error);
    };

    const handleDeleteShiftPlans = () => {
        setIsProcessingShiftPlanData(true);
        const selectedIds = shiftPlans.map(plan => plan.id);

        deleteShiftPlans({variables: {shiftPlanIds: selectedIds}})
            .then(() => {
                shouldRefreshShiftPlans();
            })
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.log("error " + error)
            });
    };

    const handleUnenrollPerformers = (performerIds, shiftPlanId) => {
        performerIds.forEach(performerId => {
            unenrollPerformer({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId
                }
            })
                .then(() => {
                    setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.PERFORMER_UNENROLLED, severity: 'success'});
                    shouldRefreshShiftPlans();
                })
                .catch(handleError);
        });
    };

    const handleEnrollPerformers = (performerIds, shiftPlanId) => {
        performerIds.forEach(performerId => {
            enrollPerformer({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId
                }
            })
                .then(() => {
                    setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.PERFORMER_ENROLLED, severity: 'success'});
                    shouldRefreshShiftPlans();
                })
                .catch(handleError);
        });
    };

    const handlePerformerEnrollment = (event, shiftPlanId) => {
        const selectedPerformerIds = event.target.value;
        const currentEnrollments = buildCurrentEnrollments(shiftPlans, shiftPlanId);

        if (isUnableToUnenrollUser(currentEnrollments, selectedPerformerIds)) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_ALREADY_GENERATED, severity: 'error'});
            return;
        }

        const unenrolledPerformerIds = buildUnenrolledPerformerIds(currentEnrollments, selectedPerformerIds);
        if (unenrolledPerformerIds.length > 0) {
            handleUnenrollPerformers(unenrolledPerformerIds, shiftPlanId);
            return;
        }

        const enrolledPerformerIds = buildEnrolledPerformerIds(currentEnrollments, selectedPerformerIds);
        if (enrolledPerformerIds.length > 0) {
            handleEnrollPerformers(enrolledPerformerIds, shiftPlanId);
        }
    };

    const handleGenerateShifts = () => {
        if (isGeneratingShifts) {
            return;
        }

        const shiftData = mapBookableServiceTypesForShiftPlan(shiftPlans, shiftBookableStatuses);
        if (!shiftData) {
            return;
        }

        setIsGeneratingShifts(true);

        generateShifts({
            variables: {shiftPlans: [shiftData]}
        })
            .then(() => {
                setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.SHIFTS_GENERATED, severity: 'success'});
                shouldRefreshShiftPlans();
            })
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.error('Error generating shifts:', error);
            })
            .finally(() => setIsGeneratingShifts(false));
    };

    const handleUpdateShifts = () => {
        if (isUpdatingShifts) {
            return;
        }

        const shiftData = mapBookableServiceTypesForShiftPlan(shiftPlans, shiftBookableStatuses);
        if (!shiftData) {
            return;
        }

        setIsUpdatingShifts(true);

        updateShifts({
            variables: {shiftPlans: [shiftData]}
        })
            .then(() => {
                setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.SHIFTS_UPDATED, severity: 'success'});
                shouldRefreshShiftPlans();
            })
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.error('Error updating shifts:', error);
            })
            .finally(() => setIsUpdatingShifts(false));
    };

    return {
        alertMessage,
        handleCloseCustomAlert,
        handleDeleteShiftPlans,
        handleGenerateShifts,
        handlePerformerEnrollment,
        handleUpdateShifts,
        isEnrolling,
        isGeneratingShifts,
        isUnenrolling,
        isUpdatingShifts
    };
}
